import React, { useEffect, useMemo, useState } from "react";

import { collection, onSnapshot, orderBy, query } from "firebase/firestore";

import { signOut } from "firebase/auth";

import { getFunctions, httpsCallable } from "firebase/functions";

import app, { auth, db } from "../firebase";

import "./AdminOrders.css";

const statuses = [
  "ALL",
  "PAID",
  "IN PRODUCTION",
  "PRINTING",
  "FRAMING",
  "READY",
  "SHIPPED",
  "DELIVERED",
];

function formatCurrency(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(Number(value || 0));
}

function formatDate(value) {
  if (!value) return "";

  let date;

  if (typeof value?.toDate === "function") {
    date = value.toDate();
  } else {
    date = new Date(value);
  }

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function getItemCount(items) {
  if (!Array.isArray(items)) {
    return 0;
  }

  return items.reduce((total, item) => total + Number(item.quantity || 1), 0);
}

function getStatusClass(status) {
  return String(status || "PAID")
    .toLowerCase()
    .replace(/\s+/g, "-");
}

/*
 * Determines whether an item actually has framing.
 */
function itemHasFraming(item) {
  if (!item) {
    return false;
  }

  const frame = item.frame;

  if (!frame) {
    return false;
  }

  const frameValue = String(frame).trim().toLowerCase();

  const noFrameValues = [
    "",
    "none",
    "no",
    "no frame",
    "no framing",
    "unframed",
    "not framed",
    "false",
    "null",
    "undefined",
    "n/a",
    "na",
  ];

  return !noFrameValues.includes(frameValue);
}

function orderHasFraming(order) {
  if (!order || !Array.isArray(order.items)) {
    return false;
  }

  return order.items.some((item) => itemHasFraming(item));
}

function normalizeOrder(doc) {
  const data = doc.data();

  return {
    id: doc.id,
    ...data,

    orderNumber: data.orderNumber || doc.id,

    customer: {
      name: data.customer?.name || "",
      email: data.customer?.email || "",
      phone: data.customer?.phone || "",
    },

    items: Array.isArray(data.items) ? data.items : [],

    shippingAddress: {
      name: data.shippingAddress?.name || "",
      address: data.shippingAddress?.address || "",
      address2: data.shippingAddress?.address2 || "",
      city: data.shippingAddress?.city || "",
      state: data.shippingAddress?.state || "",
      zip: data.shippingAddress?.zip || "",
      country: data.shippingAddress?.country || "",
    },

    stripe: {
      paymentStatus: data.stripe?.paymentStatus || "",
      paymentIntentId: data.stripe?.paymentIntentId || "",
    },

    trackingNumber: data.trackingNumber || "",
    notes: data.notes || "",
    status: data.status || "PAID",

    /*
     * PRINT SHOP
     */
    shopEmailSentAt: data.shopEmailSentAt || null,
    shopEmailMessageId: data.shopEmailMessageId || null,
  };
}

function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [activeStatus, setActiveStatus] = useState("ALL");
  const [selectedOrder, setSelectedOrder] = useState(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  /*
   * ADMIN MENU
   */
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);

  /*
   * PRINT SHOP EMAIL STATE
   */
  const [sendingToShop, setSendingToShop] = useState(false);
  const [shopMessage, setShopMessage] = useState("");
  const [shopError, setShopError] = useState("");

  const [saveMessage, setSaveMessage] = useState("");
  const [error, setError] = useState("");

  /*
   * LIVE FIRESTORE ORDERS
   */

  useEffect(() => {
    const ordersQuery = query(
      collection(db, "orders"),
      orderBy("createdAt", "desc"),
    );

    const unsubscribe = onSnapshot(
      ordersQuery,
      (snapshot) => {
        const nextOrders = snapshot.docs.map(normalizeOrder);

        setOrders(nextOrders);
        setLoading(false);
        setError("");
      },
      (err) => {
        console.error("Orders listener error:", err);

        setError("We couldn't load the orders.");
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, []);

  /*
   * LOG OUT
   */

  async function handleLogout() {
    try {
      setAdminMenuOpen(false);

      await signOut(auth);
    } catch (err) {
      console.error("Admin logout error:", err);
    }
  }

  /*
   * Only show FRAMING filter if at least one order
   * actually contains a frame.
   */

  const hasAnyFramedOrders = useMemo(() => {
    return orders.some((order) => orderHasFraming(order));
  }, [orders]);

  const visibleStatuses = useMemo(() => {
    return statuses.filter((status) => {
      if (status === "FRAMING") {
        return hasAnyFramedOrders;
      }

      return true;
    });
  }, [hasAnyFramedOrders]);

  /*
   * If FRAMING was selected and the last framed order
   * disappears/changes, return to ALL.
   */

  useEffect(() => {
    if (activeStatus === "FRAMING" && !hasAnyFramedOrders) {
      setActiveStatus("ALL");
    }
  }, [activeStatus, hasAnyFramedOrders]);

  const filteredOrders = useMemo(() => {
    if (activeStatus === "ALL") {
      return orders;
    }

    return orders.filter((order) => order.status === activeStatus);
  }, [orders, activeStatus]);

  /*
   * Status options for the currently selected order.
   */

  const selectedOrderStatuses = useMemo(() => {
    if (!selectedOrder) {
      return [];
    }

    return statuses
      .filter((status) => status !== "ALL")
      .filter((status) => {
        if (status === "FRAMING") {
          return orderHasFraming(selectedOrder);
        }

        return true;
      });
  }, [selectedOrder]);

  function updateSelectedOrder(field, value) {
    setSelectedOrder((current) => {
      if (!current) {
        return current;
      }

      return {
        ...current,
        [field]: value,
      };
    });

    setSaveMessage("");
    setError("");
  }

  /*
   * SEND ORDER TO PRINT SHOP
   */

  async function sendToPrintShop() {
    if (!selectedOrder) {
      return;
    }

    /*
     * Don't allow another send if this order
     * has already been emailed to the shop.
     */
    if (selectedOrder.shopEmailSentAt) {
      return;
    }

    setSendingToShop(true);
    setShopMessage("");
    setShopError("");

    try {
      const functions = getFunctions(app);

      const sendOrderToShop = httpsCallable(functions, "sendOrderToShop");

      await sendOrderToShop({
        orderId: selectedOrder.id,
      });

      /*
       * The Cloud Function changes the order to
       * IN PRODUCTION and records shopEmailSentAt.
       *
       * The live Firestore listener will update the
       * order automatically.
       */
      setSelectedOrder((current) => {
        if (!current) {
          return current;
        }

        return {
          ...current,
          status: "IN PRODUCTION",
          shopEmailSentAt: new Date(),
        };
      });

      setShopMessage("");
    } catch (err) {
      console.error("Send to print shop error:", err);

      /*
       * If the backend says it was already sent,
       * make that clear in the interface.
       */
      if (err?.code === "functions/already-exists") {
        setShopError("THIS ORDER HAS ALREADY BEEN SENT TO THE PRINT SHOP.");
      } else {
        setShopError(
          err?.message || "WE COULDN'T SEND THE ORDER TO THE PRINT SHOP.",
        );
      }
    } finally {
      setSendingToShop(false);
    }
  }

  /*
   * SAVE ORDER
   */

  async function saveOrder() {
    if (!selectedOrder) {
      return;
    }

    setSaving(true);
    setSaveMessage("");
    setError("");

    try {
      const functions = getFunctions(app);

      const updateOrder = httpsCallable(functions, "updateOrder");

      await updateOrder({
        orderId: selectedOrder.id,
        status: selectedOrder.status,
        trackingNumber: selectedOrder.trackingNumber || "",
        notes: selectedOrder.notes || "",
      });

      setSaveMessage("ORDER SAVED");

      setTimeout(() => {
        setSelectedOrder(null);
        setSaveMessage("");
      }, 500);
    } catch (err) {
      console.error("Save order error:", err);

      setError(err?.message || "We couldn't save the order. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="admin-orders">
      <header className="admin-orders-header">
        <div className="admin-orders-heading">
          <div className="admin-orders-admin-row">
            <p className="admin-orders-eyebrow">ADMIN</p>

            <div className="admin-orders-menu">
              <button
                type="button"
                className="admin-orders-menu-toggle"
                onClick={() => setAdminMenuOpen((current) => !current)}
                aria-label="Admin menu"
                aria-expanded={adminMenuOpen}
              >
                <span />
                <span />
                <span />
              </button>

              {adminMenuOpen && (
                <div className="admin-orders-menu-dropdown">
                  <button type="button" onClick={handleLogout}>
                    LOG OUT
                  </button>
                </div>
              )}
            </div>
          </div>

          <h1>ORDERS</h1>
        </div>

        <div className="admin-orders-count">
          {filteredOrders.length}{" "}
          {filteredOrders.length === 1 ? "ORDER" : "ORDERS"}
        </div>
      </header>

      <nav className="admin-orders-filters">
        {visibleStatuses.map((status) => (
          <button
            key={status}
            type="button"
            className={activeStatus === status ? "active" : ""}
            onClick={() => setActiveStatus(status)}
          >
            {status}
          </button>
        ))}
      </nav>

      {error && <div className="admin-orders-error">{error}</div>}

      <main className="admin-orders-content">
        {loading ? (
          <div className="admin-orders-empty">
            <p>LOADING ORDERS...</p>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="admin-orders-empty">
            <p>NO ORDERS</p>
          </div>
        ) : (
          <div className="admin-orders-table-wrap">
            <table className="admin-orders-table">
              <thead>
                <tr>
                  <th>ORDER</th>
                  <th>CUSTOMER</th>
                  <th>DATE</th>
                  <th>ITEMS</th>
                  <th>TOTAL</th>
                  <th>STATUS</th>
                  <th></th>
                </tr>
              </thead>

              <tbody>
                {filteredOrders.map((order) => (
                  <tr
                    key={order.id}
                    onClick={() => {
                      setSelectedOrder({
                        ...order,
                      });

                      setSaveMessage("");
                      setError("");
                      setShopMessage("");
                      setShopError("");
                    }}
                  >
                    <td className="order-number">#{order.orderNumber}</td>

                    <td>
                      <div className="customer-cell">
                        <strong>{order.customer.name}</strong>

                        <span>{order.customer.email}</span>
                      </div>
                    </td>

                    <td>{formatDate(order.createdAt)}</td>

                    <td>{getItemCount(order.items)}</td>

                    <td className="order-total">
                      {formatCurrency(order.total)}
                    </td>

                    <td>
                      <span
                        className={`order-status order-status-${getStatusClass(
                          order.status,
                        )}`}
                      >
                        {order.status}
                      </span>
                    </td>

                    <td className="order-view">VIEW →</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {selectedOrder && (
        <div
          className="order-overlay"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setSelectedOrder(null);
            }
          }}
        >
          <aside className="order-panel">
            <button
              type="button"
              className="order-close"
              onClick={() => setSelectedOrder(null)}
              aria-label="Close order"
            >
              ×
            </button>

            {/* HEADER */}

            <div className="order-panel-header">
              <div>
                <p className="admin-orders-eyebrow">ORDER</p>

                <h2>#{selectedOrder.orderNumber}</h2>

                <p>{formatDate(selectedOrder.createdAt)}</p>
              </div>

              <span
                className={`order-status order-status-${getStatusClass(
                  selectedOrder.status,
                )}`}
              >
                {selectedOrder.status}
              </span>
            </div>

            {/* CUSTOMER */}

            <section className="order-section">
              <h3>CUSTOMER</h3>

              <div className="order-info">
                <strong>{selectedOrder.customer.name}</strong>

                {selectedOrder.customer.email && (
                  <a href={`mailto:${selectedOrder.customer.email}`}>
                    {selectedOrder.customer.email}
                  </a>
                )}

                {selectedOrder.customer.phone && (
                  <a href={`tel:${selectedOrder.customer.phone}`}>
                    {selectedOrder.customer.phone}
                  </a>
                )}
              </div>
            </section>

            {/* ORDER DETAILS */}

            <section className="order-section">
              <h3>ORDER DETAILS</h3>

              <div className="order-items">
                {selectedOrder.items.map((item, index) => (
                  <div
                    className="order-item"
                    key={`${item.printId || item.title}-${index}`}
                  >
                    <div>
                      <strong>{item.title || "Photographic Print"}</strong>

                      {item.size && <span>{item.size}</span>}

                      {item.paper && <span>{item.paper}</span>}

                      {item.frame && <span>{item.frame}</span>}

                      <span>QTY {item.quantity || 1}</span>
                    </div>

                    <strong>
                      {formatCurrency(
                        Number(item.unitPrice || 0) *
                          Number(item.quantity || 1),
                      )}
                    </strong>
                  </div>
                ))}
              </div>

              <div className="order-summary">
                <div>
                  <span>SUBTOTAL</span>

                  <span>{formatCurrency(selectedOrder.subtotal)}</span>
                </div>

                <div>
                  <span>SHIPPING</span>

                  <span>{formatCurrency(selectedOrder.shipping)}</span>
                </div>

                <div>
                  <span>TAX</span>

                  <span>{formatCurrency(selectedOrder.tax)}</span>
                </div>

                <div className="order-summary-total">
                  <strong>TOTAL</strong>

                  <strong>{formatCurrency(selectedOrder.total)}</strong>
                </div>
              </div>
            </section>

            {/* SHIPPING */}

            <section className="order-section">
              <h3>SHIPPING ADDRESS</h3>

              <div className="order-info">
                <strong>{selectedOrder.shippingAddress.name}</strong>

                <span>{selectedOrder.shippingAddress.address}</span>

                {selectedOrder.shippingAddress.address2 && (
                  <span>{selectedOrder.shippingAddress.address2}</span>
                )}

                <span>
                  {selectedOrder.shippingAddress.city},{" "}
                  {selectedOrder.shippingAddress.state}{" "}
                  {selectedOrder.shippingAddress.zip}
                </span>

                <span>{selectedOrder.shippingAddress.country}</span>
              </div>
            </section>

            {/* STRIPE */}

            <section className="order-section">
              <h3>STRIPE PAYMENT</h3>

              <div className="order-info">
                <strong>{selectedOrder.stripe.paymentStatus || "PAID"}</strong>

                {selectedOrder.stripe.paymentIntentId && (
                  <span>{selectedOrder.stripe.paymentIntentId}</span>
                )}
              </div>
            </section>

            {/* FULFILLMENT */}

            <section className="order-section">
              <h3>FULFILLMENT</h3>

              {/* PRINT SHOP */}

              <div className="print-shop-field">
                <span className="order-field-label">PRINT SHOP</span>

                {selectedOrder.shopEmailSentAt ? (
                  <div className="print-shop-sent">
                    <div className="print-shop-sent-status">
                      <span className="print-shop-check">✓</span>

                      <strong>SENT TO PRINT SHOP</strong>
                    </div>

                    <span className="print-shop-sent-date">
                      {formatDate(selectedOrder.shopEmailSentAt)}
                    </span>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="send-print-shop-button"
                    onClick={sendToPrintShop}
                    disabled={sendingToShop || selectedOrder.status !== "PAID"}
                  >
                    {sendingToShop ? "SENDING..." : "SEND TO PRINT SHOP"}
                  </button>
                )}

                {shopMessage && (
                  <p className="print-shop-message">{shopMessage}</p>
                )}

                {shopError && <p className="print-shop-error">{shopError}</p>}
              </div>

              {/* STATUS */}

              <label className="order-field">
                <span>STATUS</span>

                <select
                  value={selectedOrder.status}
                  onChange={(event) =>
                    updateSelectedOrder("status", event.target.value)
                  }
                >
                  {selectedOrderStatuses.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </label>

              {/* TRACKING */}

              <label className="order-field">
                <span>TRACKING NUMBER</span>

                <input
                  type="text"
                  value={selectedOrder.trackingNumber}
                  onChange={(event) =>
                    updateSelectedOrder("trackingNumber", event.target.value)
                  }
                  placeholder="Enter tracking number"
                />
              </label>

              {/* NOTES */}

              <label className="order-field">
                <span>INTERNAL NOTES</span>

                <textarea
                  value={selectedOrder.notes}
                  onChange={(event) =>
                    updateSelectedOrder("notes", event.target.value)
                  }
                  placeholder="Add internal notes..."
                  rows={4}
                />
              </label>

              {saveMessage && (
                <p className="order-save-message">{saveMessage}</p>
              )}

              <button
                type="button"
                className="order-save-button"
                onClick={saveOrder}
                disabled={saving}
              >
                {saving ? "SAVING..." : "SAVE ORDER"}
              </button>
            </section>
          </aside>
        </div>
      )}
    </div>
  );
}

export default AdminOrders;
